/**
 * Super Support — Voice Recordings Tab
 *
 * Searchable list of every candidate that has voice recordings in the system.
 * Click a candidate to expand the per-question recording cards with
 * inline play, single-recording download, and ZIP-all download.
 *
 * Endpoints:
 *   GET /api/super-support/voice-candidates?search=&region=&page=
 *   GET /api/super-support/voice-recordings/{candidate_id}
 *   GET /api/super-support/voice-download/{recording_id}
 *   GET /api/super-support/voice-download-all/{candidate_id}
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Mic, Download, Play, Pause, ChevronDown, ChevronRight,
  Clock, FileAudio, AlertCircle, Loader2, Volume2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────── helpers ──
const fmtDuration = (sec) => {
  if (!sec || sec < 0) return "0s";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
};

const fmtRegion = (r) => {
  if (!r) return "—";
  const map = { jamaica: "Jamaica", barbados: "Barbados", trinidad_tobago: "Trinidad & Tobago",
                canada: "Canada", usa: "USA", panama: "Panama" };
  return map[r.toLowerCase()] || r;
};

// Inline audio player ────────────────────────────────────────────
const RecordingPlayer = ({ rec, token }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(rec.durationSeconds || 0);
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const togglePlay = async () => {
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    if (!audioRef.current) {
      setLoading(true);
      try {
        // Use native fetch (no XHR responseText coupling issue with blobs)
        const res = await fetch(`${API}/api/super-support/voice-download/${rec.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          setError(`Audio not available (HTTP ${res.status})`);
          setLoading(false);
          return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        const audio = new Audio(url);
        audio.addEventListener("timeupdate", () => {
          setProgress(audio.currentTime);
          if (audio.duration && !isNaN(audio.duration)) setDuration(audio.duration);
        });
        audio.addEventListener("ended", () => { setPlaying(false); setProgress(0); });
        audio.addEventListener("error", () => { setError("Playback failed"); setPlaying(false); });
        audioRef.current = audio;
      } catch (e) {
        setError("Failed to load audio");
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    try { await audioRef.current.play(); setPlaying(true); }
    catch (e) { setError("Play blocked"); }
  };

  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
  }, []);

  const handleDownload = async () => {
    try {
      const res = await fetch(`${API}/api/super-support/voice-download/${rec.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error(`Download failed (HTTP ${res.status}). The audio file may not exist in this environment.`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${rec.id.slice(0, 8)}_recording.webm`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      toast.error("Download failed: " + (e.message || "unknown error"));
    }
  };

  const pct = duration ? (progress / duration) * 100 : 0;

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`voice-rec-${rec.id}`}>
      <div className="flex items-center gap-3">
        <button
          onClick={togglePlay}
          disabled={loading}
          className="w-9 h-9 rounded-full bg-[#1B3A5C] text-white flex items-center justify-center hover:bg-[#0F2A47] disabled:opacity-50 shrink-0"
          data-testid={`voice-rec-${rec.id}-play`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" />
            : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-500 mb-1 flex items-center gap-3">
            <span>Rec #{rec.recordingNumber}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDuration(rec.durationSeconds)}</span>
            {rec.meanVolumeDb !== null && rec.meanVolumeDb !== undefined && (
              <span className="flex items-center gap-1"><Volume2 className="w-3 h-3" />{rec.meanVolumeDb.toFixed(1)} dB</span>
            )}
            {rec.deletedByCandidate && (
              <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-semibold">DELETED</span>
            )}
            {rec.savedAfterWarning && (
              <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs">After warning</span>
            )}
            <span className="text-slate-400">{rec.recordedAt ? new Date(rec.recordedAt).toLocaleString() : ""}</span>
          </div>
          <div className="bg-slate-200 h-1.5 rounded-full overflow-hidden">
            <div className="bg-[#1B3A5C] h-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <button
          onClick={handleDownload}
          className="px-3 py-1.5 text-xs border border-slate-300 rounded text-slate-700 hover:bg-slate-50 flex items-center gap-1 shrink-0"
          data-testid={`voice-rec-${rec.id}-download`}
        >
          <Download className="w-3 h-3" />Download
        </button>
      </div>
      {rec.transcription && (
        <div className="mt-2 pl-12 text-xs text-slate-600 italic line-clamp-3" title={rec.transcription}>
          "{rec.transcription}"
        </div>
      )}
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
  );
};

// Per-candidate expanded panel ─────────────────────────────────
const CandidateVoicePanel = ({ candidate, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/super-support/voice-recordings/${candidate.candidateId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setErr(e.response?.data?.detail || e.message);
      } finally { setLoading(false); }
    };
    load();
  }, [candidate.candidateId, token]);

  const downloadAll = async () => {
    try {
      const res = await fetch(`${API}/api/super-support/voice-download-all/${candidate.candidateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error(`ZIP download failed (HTTP ${res.status}). Audio files may not exist in this environment.`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${candidate.assessmentId || candidate.candidateId}_voice_recordings.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("ZIP download started");
    } catch (e) {
      toast.error("ZIP download failed: " + (e.message || "unknown error"));
    }
  };

  if (loading) return <div className="px-6 py-4 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading recordings…</div>;
  if (err) return <div className="px-6 py-4 text-sm text-red-600 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{err}</div>;

  const stats = data?.stats || {};
  const groups = data?.recordingsByQuestion || {};
  const qids = Object.keys(groups).sort();

  return (
    <div className="border-t border-slate-200 bg-slate-50 px-6 py-4">
      <div className="flex items-center justify-between mb-4">
        <div className="grid grid-cols-4 gap-3 flex-1 max-w-2xl">
          <StatCard label="Questions" value={stats.questionsWithRecordings} />
          <StatCard label="Active" value={stats.activeRecordings} accent="emerald" />
          <StatCard label="Deleted" value={stats.deletedRecordings} accent="red" />
          <StatCard label="Total Duration" value={fmtDuration(stats.totalDurationSeconds)} small />
        </div>
        <button
          onClick={downloadAll}
          className="ml-4 px-4 py-2 bg-[#1B3A5C] text-white rounded-md hover:bg-[#0F2A47] text-sm flex items-center gap-2 shrink-0"
          data-testid="voice-download-all-zip"
        >
          <Download className="w-4 h-4" />Download All as ZIP
        </button>
      </div>
      <div className="space-y-3">
        {qids.map(qid => {
          const recs = groups[qid];
          const totalDur = recs.reduce((s, r) => s + (r.durationSeconds || 0), 0);
          return (
            <div key={qid} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`voice-question-${qid}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold text-sm text-[#1B3A5C] flex items-center gap-2">
                    <FileAudio className="w-4 h-4" />
                    {qid === "mic_test" ? "Mic Test" : qid}
                  </div>
                  <div className="text-xs text-slate-500">
                    {recs.length} recording{recs.length !== 1 ? "s" : ""} · {fmtDuration(totalDur)}
                  </div>
                </div>
              </div>
              <div className="space-y-2 mt-2">
                {recs.map(r => <RecordingPlayer key={r.id} rec={r} token={token} />)}
              </div>
            </div>
          );
        })}
        {qids.length === 0 && (
          <div className="text-sm text-slate-500 italic">No recordings (data may have been purged).</div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent = "slate", small = false }) => {
  const tone = { slate: "text-slate-700", emerald: "text-emerald-600", red: "text-red-600", amber: "text-amber-600" }[accent] || "text-slate-700";
  return (
    <div className="bg-white border border-slate-200 rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`font-bold ${small ? "text-sm" : "text-xl"} ${tone}`}>{value ?? 0}</div>
    </div>
  );
};

// Main tab ─────────────────────────────────────────────────────
export default function VoiceRecordingsTab({ token }) {
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [data, setData] = useState({ items: [], total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/super-support/voice-candidates`, {
          params: { search: debouncedSearch, region, page, page_size: pageSize, has_voice: "true" },
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setData({ items: [], total: 0, totalPages: 0 });
      } finally { setLoading(false); }
    };
    load();
  }, [debouncedSearch, region, page, pageSize, token]);

  return (
    <div data-testid="voice-recordings-tab">
      {/* Header + search */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Mic className="w-5 h-5 text-[#1B3A5C]" />
          <h2 className="text-lg font-bold text-[#1B3A5C]">Voice Recordings</h2>
          <span className="text-xs text-slate-500 ml-2">
            {data.total} candidate{data.total !== 1 ? "s" : ""} with recordings
          </span>
        </div>
        <p className="text-xs text-slate-600 mb-3">
          Every voice recording captured by the candidate's browser is listed here — submitted, in-progress, or
          deleted by the candidate. Click a row to expand and play / download recordings.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, email, or assessment ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A5C]/30"
              data-testid="voice-search-input"
            />
          </div>
          <select
            value={region}
            onChange={e => { setRegion(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="voice-region-filter"
          >
            <option value="all">All Regions</option>
            <option value="jamaica">Jamaica</option>
            <option value="barbados">Barbados</option>
            <option value="trinidad_tobago">Trinidad & Tobago</option>
            <option value="canada">Canada</option>
            <option value="usa">USA</option>
            <option value="panama">Panama</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="voice-candidates-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3 w-8"></th>
                <th className="text-left p-3">Candidate</th>
                <th className="text-left p-3">Stage</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Region</th>
                <th className="text-right p-3">Recordings</th>
                <th className="text-right p-3">Duration</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="p-8 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…
                </td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-slate-500">No candidates with voice recordings match your filters.</td></tr>
              )}
              {!loading && data.items.map((c, i) => {
                const isOpen = expanded === c.candidateId;
                return (
                  <React.Fragment key={c.candidateId}>
                    <tr
                      className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${isOpen ? "bg-slate-50" : ""}`}
                      onClick={() => setExpanded(isOpen ? null : c.candidateId)}
                      data-testid={`voice-row-${c.candidateId}`}
                    >
                      <td className="p-3">
                        {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                      </td>
                      <td className="p-3">
                        <div className="font-semibold text-slate-800">{c.fullName}</div>
                        <div className="text-xs text-slate-500">{c.email}</div>
                      </td>
                      <td className="p-3">
                        <span className="text-xs uppercase tracking-wide text-slate-600">{c.assessmentType?.replace(/_/g, " ")}</span>
                        <div className="text-xs text-slate-400">{c.assessmentId}</div>
                      </td>
                      <td className="p-3"><StatusBadge status={c.status} /></td>
                      <td className="p-3 text-slate-700">{fmtRegion(c.region)}</td>
                      <td className="p-3 text-right">
                        <span className="font-semibold">{c.activeRecordings}</span>
                        {c.deletedRecordings > 0 && <span className="text-red-500 text-xs ml-1">+{c.deletedRecordings} deleted</span>}
                      </td>
                      <td className="p-3 text-right text-slate-600">{fmtDuration(c.totalDurationSeconds)}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan="7" className="p-0">
                          <CandidateVoicePanel candidate={c} token={token} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {data.totalPages > 1 && (
          <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between text-sm">
            <span className="text-slate-600">Page {page} of {data.totalPages} · {data.total} candidates</span>
            <div className="space-x-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 border rounded disabled:opacity-50" data-testid="voice-page-prev">Previous</button>
              <button onClick={() => setPage(p => Math.min(data.totalPages, p + 1))} disabled={page >= data.totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50" data-testid="voice-page-next">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const map = {
    not_started: ["bg-slate-100", "text-slate-600", "Not Started"],
    started:     ["bg-blue-100",  "text-blue-700",  "In Progress"],
    paused:      ["bg-amber-100", "text-amber-700", "Paused"],
    locked:      ["bg-orange-100","text-orange-700","Locked"],
    completed:   ["bg-emerald-100","text-emerald-700","Completed"],
  };
  const [bg, fg, label] = map[status] || ["bg-slate-100", "text-slate-600", status || "—"];
  return <span className={`${bg} ${fg} px-2 py-0.5 rounded text-xs font-medium`}>{label}</span>;
};
